import { History, Location, createBrowserHistory } from 'history';
import { useRouter } from 'next/router';
import {
  cloneElement,
  useEffect,
  ReactNode,
  isValidElement,
  PropsWithChildren,
  useRef,
  useState,
} from 'react';
import { __RouterContext as RouterContext } from 'react-router';
// @ts-expect-error no type declaration exists for __HistoryContext but it does exist in the transpiled js code
import { __HistoryContext as HistoryContext } from 'react-router';
import { useRouteMatch, Route, useHistory, match as Match } from 'react-router-dom';

export type AugmentedHistory = History & {
  location: Location & {
    query: { [x: string]: string };
  };
};

export type ReactRouterLayoutProps<M = { [key: string]: string }> = {
  history?: AugmentedHistory;
  match?: Match<M>;
};

// replace with @vimeo/core when that is merged
const parseQueryString = (queryString?: string): { [key: string]: string } => {
  if (typeof window === 'undefined') return {};

  const qs = queryString || window.location.search;
  return qs
    .substr(1)
    .split('&')
    .map((q) => q.split('='))
    .filter(([key]) => /\S/.test(key))
    .reduce((memo, [key, value]) => {
      return {
        ...memo,
        [key]: value,
      };
    }, {});
};

const isServer = typeof window === 'undefined';

function computeRootMatch(pathname: string) {
  return { path: '/', url: '/', params: {}, isExact: pathname === '/' };
}

/**
 * This is a more modern approach (using hooks) to the `Router` used internally by react-router@v5.2.0 which addresses some issues with the render cycle in react 18
 *
 * Original source code this was adopted from can be found here: https://github.com/remix-run/react-router/blob/v5.2.0/packages/react-router/modules/Router.js
 */
function Router({ children }: PropsWithChildren) {
  const historyRef = useRef(createBrowserHistory());
  const [location, setLocation] = useState(historyRef.current.location);

  useEffect(() => {
    return historyRef.current.listen((location) => {
      setLocation(location);
    });
  }, []);

  return (
    <RouterContext.Provider
      value={{ history: historyRef.current, location, match: computeRootMatch(location.pathname) }}
    >
      <HistoryContext.Provider value={historyRef.current}>{children}</HistoryContext.Provider>
    </RouterContext.Provider>
  );
}

/**
 * Automatically determine which router to use based on if we are on the server or not.
 */
const DynamicRouter = ({ children }: PropsWithChildren) => {
  const nextRouter = useRouter();
  if (isServer) {
    const { StaticRouter } = require('react-router');
    return <StaticRouter location={nextRouter.asPath}>{children}</StaticRouter>;
  }
  return <Router>{children}</Router>;
};

const ComponentWithRouter = ({ children }: PropsWithChildren) => {
  const match = useRouteMatch();
  const originalHistory = useHistory();
  const nextRouter = useRouter();

  // We need to update the next router when react router changes
  // so that we can inform the overall app that a route change was made
  useEffect(() => {
    return originalHistory.listen((location) => {
      // if state is undefined history.replace was called
      // use the appropriate next/router method for replace or push
      let navigate = typeof location.state === 'undefined' ? nextRouter.replace : nextRouter.push;
      navigate(location.pathname + location.search + location.hash, undefined, { shallow: true });
    });
  }, []);

  const history: AugmentedHistory = originalHistory && {
    ...originalHistory,
    location: {
      ...originalHistory.location,
      query: parseQueryString(originalHistory.location.search),
    },
  };

  if (!isValidElement(children)) {
    throw new Error('"children" is not a valid React Element for ReactRouterLayout.');
  }

  return <>{cloneElement(children, { match, history })}</>;
};

export type LayoutProps = PropsWithChildren<{ path: string | string[] }>;

/**
 * @deprecated If you are creating a new page please use native Next.js routing instead: https://nextjs.org/docs/routing/introduction
 *
 * For shared layouts see our documentation on creating layouts in Next.js https://github.com/vimeows/vimeo/blob/master/docs/frontend/NEXTJS_GUIDE.md#layouts
 */
export function ReactRouterLayout({ children, path }: LayoutProps) {
  return (
    <DynamicRouter>
      <Route path={path}>
        <ComponentWithRouter>{children}</ComponentWithRouter>
      </Route>
    </DynamicRouter>
  );
}
